import type { EChartsOption, MarkAreaComponentOption, MarkLineComponentOption, SeriesOption } from 'echarts'
import { buildChartGrid, buildDataZoom, buildMultiSeriesTooltipFormatter, buildTimeXAxis, buildValueYAxis, type ChartDataZoom } from './base-chart-options'

export const CURRENT_SERIES_NAMES = ['Corrente A', 'Corrente B', 'Corrente C', 'Média'] as const

export interface CurrentChartDataParams {
  seriesCurrentA: [number, number][]
  seriesCurrentB: [number, number][]
  seriesCurrentC: [number, number][]
  seriesAverage: [number, number][]
}

export interface BuildCurrentSeriesParams extends CurrentChartDataParams {
  /** markArea aplicado a todas as séries (detalhe do alarme). */
  alarmMarkArea?: MarkAreaComponentOption
  /** markArea com destaque por hover aplicado apenas à série Média. */
  hoveredMarkArea?: MarkAreaComponentOption
  /** markLine de limites do alarme aplicado a todas as séries. */
  alarmMarkLine?: MarkLineComponentOption
}

const CURRENT_SERIES_CONFIG: Array<{
  name: string
  color: string
}> = [
  { name: 'Corrente A', color: '#ef4444' },
  { name: 'Corrente B', color: '#f59e0b' },
  { name: 'Corrente C', color: '#10b981' },
  { name: 'Média', color: '#6366f1' },
]

/** Séries de corrente (A, B, C e média) com overlay de alarme. */
export function buildCurrentChartSeries(params: BuildCurrentSeriesParams): SeriesOption[] {
  const { alarmMarkArea, hoveredMarkArea, alarmMarkLine } = params
  const seriesData: Array<{ name: string; data: [number, number][] }> = [
    { name: CURRENT_SERIES_CONFIG[0].name, data: params.seriesCurrentA },
    { name: CURRENT_SERIES_CONFIG[1].name, data: params.seriesCurrentB },
    { name: CURRENT_SERIES_CONFIG[2].name, data: params.seriesCurrentC },
    { name: CURRENT_SERIES_CONFIG[3].name, data: params.seriesAverage },
  ]

  return seriesData.map(({ name, data }, index) => {
    const color = CURRENT_SERIES_CONFIG[index].color
    return {
      name,
      type: 'line',
      smooth: false,
      connectNulls: true,
      data,
      symbol: 'none',
      lineStyle: { width: 2, color },
      itemStyle: { color },
      markArea: alarmMarkArea ?? (index === 3 ? hoveredMarkArea : undefined),
      markLine: alarmMarkLine,
    }
  })
}

export interface BuildCurrentChartOptionParams extends BuildCurrentSeriesParams {
  dataZoom: ChartDataZoom
  sliderBottom?: number
  showMoveHandle?: boolean
  accentColor?: string
  fillerColor?: string
}

/** Opções ECharts para gráfico de corrente (A, B, C e média). */
export function buildCurrentChartOption(params: BuildCurrentChartOptionParams): EChartsOption {
  const { dataZoom, sliderBottom, showMoveHandle, accentColor = '#6366f1', fillerColor = 'rgba(99, 102, 241, 0.2)' } = params

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      formatter: buildMultiSeriesTooltipFormatter('A', 2),
    },
    legend: {
      data: [...CURRENT_SERIES_NAMES],
      top: 0,
      textStyle: { fontSize: 10 },
    },
    grid: buildChartGrid(),
    dataZoom: buildDataZoom({ dataZoom, accentColor, fillerColor, sliderBottom, showMoveHandle }),
    xAxis: buildTimeXAxis(),
    yAxis: buildValueYAxis({ unit: 'A' }),
    series: buildCurrentChartSeries(params),
  }
}
